import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { TankDecorGroup3D } from 'features/assetViz/objects/decor/TankDecorGroup3D'
import {
  createEccentricReducerGeometry,
  createEccentricReducerPlaneGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPipeEccentricReducer,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A Rich Asset Model Pipe, plotted on a cartesian plane, with the center of its
 * base at "position" and decorated with Rich Asset features specific to pipes.
 * @param model               The pipe section component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const PipeEccentricReducerSurface3D: React.FC<
  { model: GeckoPipeEccentricReducer } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale } = React.useMemo(() => {
    const scale = extractScale(groupProps.scale)

    return {
      bounds: model.componentBounds,
      scale: scale,
    }
  }, [model, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) {
      return createEccentricReducerPlaneGeometry(
        model.length.num,
        model.inlet_radius.num,
        model.outlet_radius.num
      )
    }

    return createEccentricReducerGeometry(
      model.length.num,
      model.inlet_radius.num,
      model.outlet_radius.num
    )
  }, [
    model.inlet_radius.num,
    model.length.num,
    model.outlet_radius.num,
    show3D,
  ])

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.start.x.unit)
        .minus(bounds.start.x).num

      if (!show3D) return [relX, -0.075, 0]

      return [relX, 0, -0.075]
    },
    [show3D, bounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(bounds.start.y).num

      if (!show3D) return [-0.075, relY, 0]

      const segment = relY / bounds.height.num
      const angle = 2.0 * Math.PI * segment

      return [
        0,
        model.inlet_radius.num * Math.sin(angle),
        model.inlet_radius.num * Math.cos(angle) + model.inlet_radius.num,
      ]
    },
    [bounds, show3D, model.inlet_radius.num]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      <TankDecorGroup3D>
        {/* Y ruler -------------------------------------------------------- */}
        <Ruler3D
          anchor={'bottom'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks={!show3D}
          includeStart
          includeEnd
          getTickPosition={show3D ? positionYTick : positionXTick}
        />
      </TankDecorGroup3D>
      {/* X ruler ---------------------------------------------------------- */}
      <TankDecorGroup3D>
        <Ruler3D
          anchor={'left'}
          start={model.componentBounds.start.x}
          end={model.componentBounds.end.x}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={show3D ? positionXTick : positionYTick}
        />
      </TankDecorGroup3D>
    </ModelSurface3D>
  )
}
